import React, { useState, useEffect } from 'react';
import cacheManager from '../CacheManager';
import { showSuccess, showError } from '../utils/toast';

const TELEFONE_MAX_LENGTH = 20;

const PgMeuPerfil = () => {
  const [clienteData, setClienteData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [telefoneError, setTelefoneError] = useState(false);
  const [senhaData, setSenhaData] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmarSenha: ''
  });
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    cpf: '',
    nascimento: '',
    cli_endereco: '',
    cli_cidade: '',
    cli_uf: '',
    cli_cep: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const cliente = await cacheManager.getClienteData();
      setClienteData(cliente);
      if (cliente) {
        setFormData({
          nome: cliente.nome || '',
          email: cliente.email || '',
          telefone: cliente.telefone || '',
          cpf: cliente.cpf || '',
          nascimento: cliente.nascimento || '',
          cli_endereco: cliente.cli_endereco || '',
          cli_cidade: cliente.cli_cidade || '',
          cli_uf: cliente.cli_uf || '',
          cli_cep: cliente.cli_cep || ''
        });
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'telefone' && value.length > TELEFONE_MAX_LENGTH) {
      setTelefoneError(true);
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do telefone quando o usuário digitar
    if (name === 'telefone') {
      setTelefoneError(false);
    }
  };

  const formatarTelefone = (valor) => {
    // Remove tudo que não é número
    const numeros = valor.replace(/\D/g, '');
    
    if (numeros.length === 8) {
      // Formato: 1234-5678
      return numeros.replace(/(\d{4})(\d{4})/, '$1-$2');
    } else if (numeros.length === 9) {
      // Formato: 91234-5678
      return numeros.replace(/(\d{5})(\d{4})/, '$1-$2');
    } else if (numeros.length === 10) {
      // Formato: (21) 1234-5678
      return numeros.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else if (numeros.length === 11) {
      // Formato: (21) 91234-5678
      return numeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    
    // Para números internacionais (ou outros formatos), mantém o valor informado
    return valor;
  };

  const handleBlurTelefone = (e) => {
    const valor = e.target.value.trim();
    const numeros = valor.replace(/\D/g, '');
    
    // Se estiver vazio, não aplica formatação nem erro
    if (valor === '') {
      setTelefoneError(false);
      return;
    }

    if (valor.length > TELEFONE_MAX_LENGTH) {
      setTelefoneError(true);
      return;
    }

    if (numeros.length === 0) {
      setTelefoneError(true);
      return;
    }
    
    const valorFormatado = formatarTelefone(valor);

    setFormData(prev => ({
      ...prev,
      telefone: valorFormatado
    }));
    setTelefoneError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if ((formData.telefone || '').length > TELEFONE_MAX_LENGTH) {
      setTelefoneError(true);
      showError('Telefone deve ter no máximo 20 caracteres');
      return;
    }

    setIsSaving(true);

    try {
      const sessionData = cacheManager.getSessionData();
      
      const response = await fetch('https://api.faeworks.com.br/cliente/update.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_cliente: sessionData.id_cliente,
          ...formData
        }),
      });

      const data = await response.json();

      if (response.ok) {
        showSuccess('Dados atualizados com sucesso!');
        cacheManager.invalidateClienteData();
        await loadData();
        setIsEditing(false);
      } else {
        showError(data.message || 'Erro ao atualizar dados');
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      showError('Erro ao salvar dados');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setSenhaData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (senhaData.novaSenha !== senhaData.confirmarSenha) {
      showError('As senhas não coincidem');
      return;
    }

    if (senhaData.novaSenha.length < 4) {
      showError('A senha deve ter no mínimo 4 caracteres');
      return;
    }

    try {
      const sessionData = cacheManager.getSessionData();
      
      const response = await fetch('https://api.faeworks.com.br/cliente/update.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_cliente: sessionData.id_cliente,
          senha: senhaData.novaSenha
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        showSuccess('Senha alterada com sucesso!');
        setShowPasswordModal(false);
        setSenhaData({
          senhaAtual: '',
          novaSenha: '',
          confirmarSenha: ''
        });
      } else {
        showError(data.message || 'Erro ao alterar senha');
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      showError('Erro ao alterar senha');
    }
  };

  const handleCancelEdit = () => {
    // Restaurar dados originais
    if (clienteData) {
      setFormData({
        nome: clienteData.nome || '',
        email: clienteData.email || '',
        telefone: clienteData.telefone || '',
        cpf: clienteData.cpf || '',
        nascimento: clienteData.nascimento || '',
        cli_endereco: clienteData.cli_endereco || '',
        cli_cidade: clienteData.cli_cidade || '',
        cli_uf: clienteData.cli_uf || '',
        cli_cep: clienteData.cli_cep || ''
      });
    }
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row mb-4">
        <div className="col-md-8">
          <h2>Meu Perfil</h2>
          <p className="text-muted">Visualize e edite suas informações pessoais</p>
        </div>
        <div className="col-md-4 text-end">
          {!isEditing ? (
            <button 
              className="btn btn-primary"
              onClick={() => setIsEditing(true)}
            >
              <i className="bi bi-pencil"></i> Editar Perfil
            </button>
          ) : (
            <>
              <button 
                className="btn btn-secondary me-2"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? 'Salvando...' : 'Salvar'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-md-8">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                {/* Dados Pessoais */}
                <h5 className="mb-3 pb-2 border-bottom">Dados Pessoais</h5>
                
                <div className="row mb-3">
                  <div className="col-md-12">
                    <label className="form-label">Nome Completo</label>
                    <input
                      type="text"
                      className="form-control"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      disabled={!isEditing}
                      required
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">E-mail</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={!isEditing}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">CPF</label>
                    <input
                      type="text"
                      className="form-control"
                      name="cpf"
                      value={formData.cpf}
                      onChange={handleChange}
                      disabled={true}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Telefone</label>
                    <input
                      type="text"
                      className={`form-control ${telefoneError ? 'is-invalid' : ''}`}
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleChange}
                      onBlur={handleBlurTelefone}
                      disabled={!isEditing}
                      maxLength={TELEFONE_MAX_LENGTH}
                    />
                    {telefoneError && (
                      <div className="invalid-feedback d-block">
                        Telefone deve ter no máximo 20 caracteres
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Nascimento</label>
                    <input
                      type="date"
                      className="form-control"
                      name="nascimento"
                      value={formData.nascimento}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  </div>
                </div>

                {/* Endereço */}
                <h5 className="mb-3 pb-2 border-bottom mt-4">Endereço</h5>

                <div className="row mb-3">
                  <div className="col-md-12">
                    <label className="form-label">Endereço</label>
                    <input
                      type="text"
                      className="form-control"
                      name="cli_endereco"
                      value={formData.cli_endereco}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-5">
                    <label className="form-label">Cidade</label>
                    <input
                      type="text"
                      className="form-control"
                      name="cli_cidade"
                      value={formData.cli_cidade}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Estado</label>
                    <input
                      type="text"
                      className="form-control"
                      name="cli_uf"
                      value={formData.cli_uf}
                      onChange={handleChange}
                      disabled={!isEditing}
                      maxLength="2"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">CEP</label>
                    <input
                      type="text"
                      className="form-control"
                      name="cli_cep"
                      value={formData.cli_cep}
                      onChange={handleChange}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Informações Adicionais */}
        <div className="col-md-4">
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <h6 className="card-title">Informações da Conta</h6>
              <hr />
              <div className="mb-2">
                <small className="text-muted">ID do Cliente</small>
                <p className="mb-0 fw-bold">#{clienteData?.id_cliente}</p>
              </div>
              <div className="mb-2">
                <small className="text-muted">Data de Cadastro</small>
                <p className="mb-0">
                  {clienteData?.data_cadastro ? 
                    new Date(clienteData.data_cadastro).toLocaleDateString('pt-BR') : 
                    'N/A'
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h6 className="card-title">Segurança</h6>
              <hr />
              <button 
                className="btn btn-outline-primary w-100 mb-2"
                onClick={() => setShowPasswordModal(true)}
              >
                <i className="bi bi-key"></i> Alterar Senha
              </button>
              <p className="text-muted small mb-0">
                Recomendamos alterar sua senha periodicamente para manter sua conta segura.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Alterar Senha */}
      {showPasswordModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Alterar Senha</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowPasswordModal(false)}
                ></button>
              </div>
              <form onSubmit={handlePasswordSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Nova Senha</label>
                    <input
                      type="password"
                      className="form-control"
                      name="novaSenha"
                      value={senhaData.novaSenha}
                      onChange={handlePasswordChange}
                      required
                      minLength="4"
                      placeholder="Digite a nova senha"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Confirmar Nova Senha</label>
                    <input
                      type="password"
                      className="form-control"
                      name="confirmarSenha"
                      value={senhaData.confirmarSenha}
                      onChange={handlePasswordChange}
                      required
                      minLength="4"
                      placeholder="Confirme a nova senha"
                    />
                  </div>
                  <div className="alert alert-info small mb-0">
                    <i className="bi bi-info-circle"></i> A senha deve ter no mínimo 4 caracteres.
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowPasswordModal(false)}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <i className="bi bi-check-circle"></i> Alterar Senha
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PgMeuPerfil;
